import React, { useState, useMemo, useEffect, useCallback, useRef } from "react";
import PropTypes from "prop-types";
import withRouter from "components/Common/withRouter";
import { Button, Card, CardBody, Col, Container, Input, Row } from "reactstrap";
import Select from "react-select";
import TableContainer from "components/Common/TableContainer";
import Loaders from "components/Common/Loaders";
import { useNavigate, useParams } from "react-router-dom";
import { DEFAULT_PAGE_SIZE, urlEndPoint } from "helpers/GeneralHelper";
import { useCommonTableSettingsPageSize } from "hooks/useSettingsReadyTablePageSize";
import {
  fetchCommonWorkShiftList,
  fetchCommonUserBlockStatusList,
  mapUserBlockStatusApiToSelectOptions,
  resetUserSystemBlock,
} from "model/UserModel";
import {
  submitCompanyUserFilterData,
  fetchCompanyUserTableAndFilterSchema,
  deleteCompanyUser,
} from "model/CompanyUserModel";
import { submitUserTypeFilterData } from "model/UserTypeModel";
import { toast } from "react-toastify";
import Swal from "sweetalert2";
import { useCommonFilterOptions } from "libs/context/CommonFilterOptionsContext";
import { usePermissionContext } from "libs/context/PermissionContext";

import "./company-user.scss";

const unwrapArray = (payload) => {
  if (Array.isArray(payload)) return payload;
  const payloadRoot = payload?.data ?? payload;
  if (Array.isArray(payloadRoot)) return payloadRoot;
  const nestedRecords =
    payloadRoot?.records ?? payloadRoot?.rows ?? payloadRoot?.items;
  return Array.isArray(nestedRecords) ? nestedRecords : [];
};

const normalizeId = (value) => String(value ?? "").trim();

const selectZIndexStyles = {
  menuPortal: (base) => ({ ...base, zIndex: 9999 }),
  menu: (base) => ({ ...base, zIndex: 9999 }),
};

const defaultTableMetaData = [
  { Header: "ID", accessor: "id" },
  { Header: "Name", accessor: "name" },
  { Header: "Email", accessor: "email" },
  { Header: "Type", accessor: "type" },
  { Header: "Reg Date", accessor: "regDate" },
  { Header: "Online", accessor: "online" },
  { Header: "Shift", accessor: "shift" },
  { Header: "Status", accessor: "status" },
];

const EMPTY_COMPANY_USER_FILTERS = {
  userName: "",
  startDate: "",
  endDate: "",
  blockStatus: "",
  userTypeId: "",
  workShiftId: "",
};

const CompanyUser = () => {
  const { fetchMetadataSchema } = useCommonFilterOptions();
  const { permissionData } = usePermissionContext() || {};
  const canAddCompanyUser = Boolean(permissionData?.ClientCompanyAddAccess);
  const [filterForm, setFilterForm] = useState(() => ({ ...EMPTY_COMPANY_USER_FILTERS }));
  const [blockStatusOptions, setBlockStatusOptions] = useState([]);
  const [userTypeOptions, setUserTypeOptions] = useState([]);
  const [workShiftOptions, setWorkShiftOptions] = useState([]);

  const [userData, setUserData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [tableMetaData, setTableMetaData] = useState([]);
  const [requestPayload, setRequestPayload] = useState({ pageNumber: 1, pageSize: DEFAULT_PAGE_SIZE });
  const [paginationMeta, setPaginationMeta] = useState({
    pageNumber: 1,
    pageSize: DEFAULT_PAGE_SIZE,
    totalPages: 0,
    totalRecords: 0,
  });

  const navigate = useNavigate();
  const { companyId: companyIdParam } = useParams();
  const companyId = normalizeId(companyIdParam);
  const { pageSize: settingsPageSize, settingsReady } = useCommonTableSettingsPageSize("CompanyUser");
  const initialFilterFetchedRef = useRef(false);

  const getSelectedOption = (options, selectedValue) =>
    options.find((option) => String(option?.value) === String(selectedValue)) ||
    null;

  const buildUserSystemFiltersFromSnapshot = (filterSnapshot) => {
    const filters = {};
    const nameSearch = String(filterSnapshot.userName || "").trim();
    if (nameSearch) {
      filters.nameContains = nameSearch;
      filters.search = nameSearch;
    }
    if (filterSnapshot.startDate) filters.fromDate = filterSnapshot.startDate;
    if (filterSnapshot.endDate) filters.toDate = filterSnapshot.endDate;
    if (filterSnapshot.blockStatus) filters.userStatus = filterSnapshot.blockStatus;
    if (filterSnapshot.userTypeId) filters.userTypeId = filterSnapshot.userTypeId;
    if (filterSnapshot.workShiftId) filters.workShiftId = filterSnapshot.workShiftId;
    return filters;
  };

  const buildUserSystemFiltersFromForm = useCallback(() => {
    return buildUserSystemFiltersFromSnapshot(filterForm);
  }, [filterForm]);

  const loadFilterDropdowns = useCallback(async () => {
    try {
      const [
        userTypesApiResponse,
        workShiftsApiResponse,
        blockStatusesApiResponse,
      ] = await Promise.all([
        // canAddCompanyUser && submitUserTypeFilterData({}),
        fetchCommonWorkShiftList(),
        fetchCommonUserBlockStatusList(),
      ]);

      const userTypeRows = unwrapArray(
        userTypesApiResponse?.data ?? userTypesApiResponse
      );
      setUserTypeOptions(
        userTypeRows
          .map((userTypeRow) => ({
            value: normalizeId(userTypeRow?.id ?? userTypeRow?._id),
            label:
              userTypeRow?.name ??
              userTypeRow?.title ??
              normalizeId(userTypeRow?.id),
          }))
          .filter((option) => option.value)
      );

      const workShiftRows = unwrapArray(
        workShiftsApiResponse?.data ?? workShiftsApiResponse
      );
      setWorkShiftOptions(
        workShiftRows
          .map((workShiftRow) => {
            const shiftId = normalizeId(workShiftRow?.id ?? workShiftRow?._id);
            const shiftLabel =
              workShiftRow?.display ??
              workShiftRow?.name ??
              workShiftRow?.title ??
              workShiftRow?.workShiftTitle ??
              workShiftRow?.workShiftTimeDisplay ??
              shiftId;
            return { value: shiftId, label: String(shiftLabel) };
          })
          .filter((option) => option.value)
      );

      setBlockStatusOptions(
        mapUserBlockStatusApiToSelectOptions(
          blockStatusesApiResponse?.data ?? blockStatusesApiResponse
        )
      );
    } catch (error) {
      toast.error(error?.message || "Unable to load filter options");
    }
  }, [canAddCompanyUser]);

  const getUserTableSchema = async () => {
    const metadataResponse = await fetchMetadataSchema(
      "metadata:companyUser",
      () => fetchCompanyUserTableAndFilterSchema(companyId || undefined)
    );
    const columns = metadataResponse?.data?.table?.columns || [];
    if (Array.isArray(columns) && columns.length) {
      setTableMetaData(
        columns.map((col) => ({
          Header: col?.title,
          accessor: col?.field,
          type: col?.type,
        }))
      );
      return;
    }
    setTableMetaData(defaultTableMetaData);
  };

  const filteredData = useMemo(() => userData, [userData]);

  const getUserList = async (payloadOverrides = {}, filterSnapshot = null) => {
    if (!companyId) {
      setLoading(false);
      return;
    }
    setLoading(true);
    const filterBody = filterSnapshot
      ? buildUserSystemFiltersFromSnapshot(filterSnapshot)
      : buildUserSystemFiltersFromForm();
    const requestPayloadForApi = {
      ...filterBody,
      pageNumber: payloadOverrides.pageNumber ?? requestPayload.pageNumber ?? 1,
      pageSize: payloadOverrides.pageSize ?? requestPayload.pageSize ?? DEFAULT_PAGE_SIZE,
      ...(companyId ? { companyId } : {}),
      ...payloadOverrides,
    };

    const filterResponse = await submitCompanyUserFilterData(
      requestPayloadForApi
    );
    const paginationPayload = filterResponse?.data || {};
    const rawUserRows =
      paginationPayload?.records ??
      paginationPayload?.rows ??
      paginationPayload ??
      [];
    const normalizedUserRows = (
      Array.isArray(rawUserRows) ? rawUserRows : []
    ).map((userRow) => ({
      ...userRow,
      id: userRow?.id ?? userRow?._id ?? userRow?.userId ?? "",
      name: userRow?.name ?? userRow?.userName ?? "",
      email: userRow?.email ?? "",
      type: userRow?.type ?? userRow?.userType ?? "",
      regDate:
        userRow?.regDate ??
        userRow?.reg_date ??
        userRow?.createdOn ??
        "",
      online: userRow?.online ?? userRow?.isOnline ?? "",
      shift: userRow?.shift ?? userRow?.workShift ?? "",
      status: userRow?.status ?? "",
      blocked: Boolean(userRow?.blocked),
      actions: userRow?.actions ?? {},
    }));

    if (filterResponse) {
      setUserData(normalizedUserRows);
      setPaginationMeta({
        pageNumber:
          Number(paginationPayload?.pageNumber) ||
          Number(requestPayloadForApi?.pageNumber) ||
          1,
        pageSize:
          Number(paginationPayload?.pageSize) ||
          Number(requestPayloadForApi?.pageSize) ||
          100,
        totalPages: Number(paginationPayload?.totalPages) || 0,
        totalRecords: Number(paginationPayload?.totalRecords) || 0,
      });
      setRequestPayload(requestPayloadForApi);
    } else {
      setUserData([]);
    }
    setLoading(false);
  };

  useEffect(() => {
    loadFilterDropdowns();
  }, [loadFilterDropdowns]);

  useEffect(() => {
    if (!companyId) return;
    getUserTableSchema();
  }, [companyId]);

  useEffect(() => {
    initialFilterFetchedRef.current = false;
  }, [companyId]);

  useEffect(() => {
    if (!companyId || !settingsReady || initialFilterFetchedRef.current) return;
    initialFilterFetchedRef.current = true;
    const pageSize = Number(settingsPageSize) || DEFAULT_PAGE_SIZE;
    setRequestPayload((previous) => ({ ...previous, pageSize }));
    setPaginationMeta((previous) => ({ ...previous, pageSize }));
    setFilterForm({ ...EMPTY_COMPANY_USER_FILTERS });
    getUserList({ pageNumber: 1, pageSize }, { ...EMPTY_COMPANY_USER_FILTERS });
  }, [companyId, settingsReady, settingsPageSize]);

  const handleFilterFieldChange = (event) => {
    const { name, value } = event.target;
    setFilterForm((previousFilters) => ({ ...previousFilters, [name]: value }));
  };

  const handleFilterSelectChange = (fieldName, selectedOption) => {
    setFilterForm((previousFilters) => ({
      ...previousFilters,
      [fieldName]: selectedOption?.value ?? "",
    }));
  };

  const handleSearchClick = async () => {
    await getUserList({ pageNumber: 1 });
  };

  const resolveRowId = (row) =>
    String(row?.id ?? row?._id ?? row?.userId ?? "").trim();

  const handleEdit = (row) => {
    const id = resolveRowId(row);
    if (!id) {
      toast.error("User id is missing");
      return;
    }
    if (!companyId) return;
    navigate(
      `/${urlEndPoint.companyUserAddEdit}/${encodeURIComponent(companyId)}/${encodeURIComponent(id)}`
    );
  };

  const handleDelete = async (row) => {
    const uid = resolveRowId(row);
    if (!uid) {
      toast.error("User id is missing");
      return;
    }
    if (!companyId) return;
    const swalResult = await Swal.fire({
      title: "Remove user from company?",
      text: "This will remove the user association for this company.",
      icon: "warning",
      showCancelButton: true,
      confirmButtonText: "Yes, delete",
      cancelButtonText: "Cancel",
      reverseButtons: true,
    });
    if (!swalResult?.isConfirmed) return;
    const res = await deleteCompanyUser(uid, companyId);
    if (res?.success !== false && (res?.success || res?.isSuccess || res?.data != null)) {
      toast.success(res?.message || "Deleted successfully");
      await getUserList({ pageNumber: paginationMeta?.pageNumber || 1 });
      return;
    }
    toast.error(res?.message || "Unable to delete");
  };

  const handleResetBlock = async (row) => {
    if (!row?.actions?.canResetBlock || !row?.blocked) return;
    const id = resolveRowId(row);
    if (!id) {
      toast.error("User id is missing");
      return;
    }
    const swalResult = await Swal.fire({
      title: "Reset block?",
      text: "Are you sure you want to reset the block for this user?",
      icon: "warning",
      showCancelButton: true,
      confirmButtonText: "OK",
      cancelButtonText: "Cancel",
      reverseButtons: true,
    });
    if (!swalResult?.isConfirmed) return;

    const response = await resetUserSystemBlock(id);
    if (response?.success || response?.isSuccess || response?.status) {
      toast.success(response?.message || "Block reset successfully");
      await getUserList({ pageNumber: paginationMeta?.pageNumber || 1 });
      return;
    }
    toast.error(response?.message || "Unable to reset block");
  };

  const handlePageChange = async (page) => {
    await getUserList({ pageNumber: page });
  };

  const handleRowsPerPageChange = async (rows) => {
    await getUserList({ pageNumber: 1, pageSize: rows });
  };

  return (
    <React.Fragment>
      <div className="page-content company-user-page">
        <Container fluid>
          <Card>
            <CardBody>
              <div className="d-flex justify-content-between align-items-center">
                <h5 className="fw-bold mb-3">
                  Company users
                  {/* {companyId ? ` (Company ID: ${companyId})` : ""} */}
                </h5>
                <Col xs="12" md="4" className="text-md-end mt-2 mt-md-0">
                  <Button
                    color="primary"
                    disabled={!companyId}
                    onClick={() =>
                      companyId &&
                      navigate(
                        `/${urlEndPoint.companyUserAddEdit}/${encodeURIComponent(companyId)}`
                      )
                    }
                  >
                    + Create
                  </Button>
                </Col>
              </div>
              <hr />

              {!companyId ? (
                <p className="text-muted mb-0">
                  Missing company id in the path (expected <code>/company-user/[id]</code>). Open this page from{" "}
                  <Button color="link" className="p-0 align-baseline" onClick={() => navigate(`/${urlEndPoint.company}`)}>
                    Company
                  </Button>{" "}
                  and use the users action for a company.
                </p>
              ) : null}

              {companyId ? (
                <div className="company-user-filter-strip mb-4">
                  <Row className="g-2 g-md-3 align-items-end">
                    <Col xs={12} sm={3} md={3} lg={4}>
                      <span className="company-user-filter-label">User Name</span>
                      <Input
                        type="text"
                        name="userName"
                        placeholder="User Name"
                        className="category-search-input"
                        value={filterForm.userName}
                        onChange={(e) => {
                          const nextValue = e?.target?.value ?? "";
                          setFilterForm((previousFilters) => ({
                            ...previousFilters,
                            userName: nextValue,
                          }));
                        }}
                        onKeyDown={(e) => {
                          if (e.key === "Enter") {
                            handleSearchClick();
                          }
                        }}
                      />
                    </Col>
                    <Col xs={12} sm={3} md={3} lg={2}>
                      <span className="company-user-filter-label">Start date</span>
                      <Input
                        type="date"
                        name="startDate"
                        className="category-search-input"
                        value={filterForm.startDate}
                        onChange={handleFilterFieldChange}
                      />
                    </Col>
                    <Col xs={12} sm={3} md={3} lg={2}>
                      <span className="company-user-filter-label">End date</span>
                      <Input
                        type="date"
                        name="endDate"
                        className="category-search-input"
                        value={filterForm.endDate}
                        onChange={handleFilterFieldChange}
                      />
                    </Col>
                    <Col xs={12} sm={3} md={3} lg={3}>
                      <span className="company-user-filter-label">Block Status</span>
                      <Select
                        options={blockStatusOptions}
                        className="select2-selection"
                        placeholder="Block Status"
                        isClearable
                        value={getSelectedOption(blockStatusOptions, filterForm.blockStatus)}
                        onChange={(opt) => handleFilterSelectChange("blockStatus", opt)}
                        menuPortalTarget={typeof document !== "undefined" ? document.body : null}
                        menuPosition="fixed"
                        styles={selectZIndexStyles}
                      />
                    </Col>
                    <Col xs={12} sm={3} md={3} lg={1}>
                      <span className="company-user-filter-label d-none d-lg-block">&nbsp;</span>
                      <Button
                        color="primary"
                        type="button"
                        className="company-user-search-btn w-100 d-flex align-items-center justify-content-center gap-1"
                        onClick={handleSearchClick}
                      >
                        <i className="fa fa-search" aria-hidden="true" />
                        Search
                      </Button>
                    </Col>
                  </Row>
                </div>
              ) : null}

              {companyId && !loading && tableMetaData?.length ? (
                <TableContainer
                  columns={tableMetaData}
                  data={filteredData}
                  isActionRequired
                  onEdit={handleEdit}
                  onDelete={handleDelete}
                  onResetBlock={handleResetBlock}
                  isCheckboxLeftSide={false}
                  isPagination
                  currentPage={paginationMeta?.pageNumber}
                  rowsPerPage={paginationMeta?.pageSize}
                  totalPages={paginationMeta?.totalPages}
                  totalRecords={paginationMeta?.totalRecords}
                  onPageChange={handlePageChange}
                  onPageSizeChange={handleRowsPerPageChange}
                  pageName="CompanyUser"
                />
              ) : companyId ? (
                <Loaders />
              ) : null}
            </CardBody>
          </Card>
        </Container>
      </div>
    </React.Fragment>
  );
};

CompanyUser.propTypes = {
  orders: PropTypes.array,
  onGetOrders: PropTypes.func,
};

export default withRouter(CompanyUser);
